import { useEffect, useSyncExternalStore } from 'react';

export type ThemeChoice = 'light' | 'dark' | 'system';

export const THEME_STORAGE_KEY = 'overseer.theme';

const read = (): ThemeChoice => {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);

    return saved === 'light' || saved === 'dark' ? saved : 'system';
  } catch {
    return 'system';
  }
};

/**
 * Applies a theme choice to the document.
 *
 * `system` **removes** the attribute rather than writing a resolved value, so the
 * page keeps following the OS preference as it changes. Writing `data-theme` at
 * that point would freeze whatever the OS happened to be at page load, and the
 * user's "System" choice would quietly become "whatever it was this morning".
 *
 * `index.html` runs the same logic inline before first paint, so a dark-mode
 * user does not see a white flash. The two must agree on the storage key.
 */
const apply = (choice: ThemeChoice): void => {
  if (choice === 'system') {
    delete document.documentElement.dataset.theme;
  } else {
    document.documentElement.dataset.theme = choice;
  }
};

/**
 * One choice for the whole page, not one per component.
 *
 * The header toggle, the account screen and the server's saved choice all change
 * it, and each control must show what the others set.
 */
let current: ThemeChoice = read();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);

  return () => listeners.delete(listener);
};

const snapshot = (): ThemeChoice => current;

/**
 * Applies a theme in this browser and keeps it in localStorage, which is what
 * `index.html` reads before the first paint. Saving it for the person is
 * `useChoosePreference` in `preferences.ts`.
 */
export const setThemeChoice = (next: ThemeChoice): void => {
  current = next;
  apply(next);

  try {
    if (next === 'system') {
      localStorage.removeItem(THEME_STORAGE_KEY);
    } else {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    }
  } catch {
    // Applies for this session even if it cannot be remembered.
  }

  listeners.forEach((listener) => listener());
};

/** The theme in force. Change it with `useChoosePreference().theme`. */
export const useTheme = (): ThemeChoice => {
  const choice = useSyncExternalStore(subscribe, snapshot, snapshot);

  useEffect(() => {
    apply(choice);
  }, [choice]);

  return choice;
};
